import cognitoService from './cognito';

// API base URL (same host as GraphQL endpoint, without /graphql)
const API_BASE_URL = (import.meta.env.VITE_GRAPHQL_ENDPOINT || 'https://api.work-it.com.ar/graphql').replace('/graphql', '');

export interface AuthUser {
  username: string;
  email: string;
  role: 'ADMINISTRATOR';
  cognitoSub?: string;
}

/**
 * Check if input looks like an email
 */
const isEmail = (input: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
};

/**
 * Lookup phone number by email via API
 */
const lookupPhoneByEmail = async (email: string): Promise<string | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/lookup-by-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.toLowerCase().trim() }),
    });

    const data = await response.json();

    if (data.success && data.username) {
      return data.username; // This is the phone number
    }

    return null;
  } catch (error) {
    console.error('Email lookup error:', error);
    return null;
  }
};

/**
 * Login with Cognito credentials
 * Can use either email or phone number as username
 */
export const login = async (username: string, password: string): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
  try {
    let cognitoUsername = username;

    // If user entered email, resolve to phone number first
    if (isEmail(username)) {
      const phone = await lookupPhoneByEmail(username);
      if (!phone) {
        return {
          success: false,
          error: 'User not found',
        };
      }
      cognitoUsername = phone;
    }

    const result = await cognitoService.signIn(cognitoUsername, password);

    if (result.success && result.tokens) {
      // Get user attributes to verify role
      try {
        const attributes = await cognitoService.getUserAttributes();

        // Store user info
        const user: AuthUser = {
          username: attributes.email || username,
          email: attributes.email || '',
          role: 'ADMINISTRATOR',
          cognitoSub: attributes.sub,
        };

        localStorage.setItem('admin_user', JSON.stringify(user));

        return { success: true };
      } catch (error) {
        console.error('Failed to get user attributes:', error);
        return { success: true }; // Still allow login even if we can't get attributes
      }
    }

    return {
      success: false,
      error: result.error || 'Login failed',
      errorCode: result.errorCode,
    };
  } catch (error: any) {
    console.error('Login error:', error);
    return {
      success: false,
      error: error.message || 'Login failed',
    };
  }
};

/**
 * Logout and clear all tokens
 */
export const logout = async (): Promise<void> => {
  await cognitoService.signOut();
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  return await cognitoService.isAuthenticated();
};

/**
 * Get current user from localStorage
 */
export const getCurrentUser = (): AuthUser | null => {
  const userStr = localStorage.getItem('admin_user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};
